import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Download, Printer, RotateCcw } from 'lucide-react';
interface ESASData {
  patientName: string;
  birthDate: string;
  assessmentBy: string;
  date: string;
  symptoms: {
    schmerz: number;
    erschoepfung: number;
    uebelkeit: number;
    verstopfung: number;
    angst: number;
    benommenheit: number;
    appetitlosigkeit: number;
    wohlbefinden: number;
    atemnot: number;
    weitereDescherden: number;
  };
  weitereDescherdenText: string;
  visum: string;
}
const symptoms = [{
  key: 'schmerz',
  label: 'Schmerz',
  description: 'Schmerzintensität'
}, {
  key: 'erschoepfung',
  label: 'Erschöpfung',
  description: 'Müdigkeit und Kraftlosigkeit'
}, {
  key: 'uebelkeit',
  label: 'Übelkeit',
  description: 'Übelkeit und Erbrechen'
}, {
  key: 'verstopfung',
  label: 'Verstopfung',
  description: 'Probleme mit dem Stuhlgang'
}, {
  key: 'angst',
  label: 'Angst (Innere Unruhe)',
  description: 'Angstgefühle und innere Unruhe'
}, {
  key: 'benommenheit',
  label: 'Benommenheit (Schwindel)',
  description: 'Schwindel und Benommenheit'
}, {
  key: 'appetitlosigkeit',
  label: 'Appetitlosigkeit',
  description: 'Mangelnder Appetit'
}, {
  key: 'wohlbefinden',
  label: 'Wohlbefinden',
  description: 'Allgemeines Wohlbefinden (0 = sehr gut, 10 = sehr schlecht)'
}, {
  key: 'atemnot',
  label: 'Atemnot',
  description: 'Schwierigkeiten beim Atmen'
}, {
  key: 'weitereDescherden',
  label: 'Weitere Beschwerden',
  description: 'Andere Symptome oder Beschwerden'
}];
const getScoreColor = (score: number) => {
  if (score <= 2) return 'bg-green-100 border-green-300 text-green-800';
  if (score <= 6) return 'bg-yellow-100 border-yellow-300 text-yellow-800';
  return 'bg-red-100 border-red-300 text-red-800';
};
const getScoreDescription = (score: number) => {
  if (score <= 2) return 'Symptom vorhanden, jedoch ohne Behandlungsbedarf';
  if (score <= 6) return 'Symptom vorhanden, sollte behandelt werden';
  return 'Symptom vorhanden, sofortiger Handlungsbedarf';
};
export const ESASAssessment: React.FC = () => {
  const [esasData, setESASData] = useState<ESASData>({
    patientName: '',
    birthDate: '',
    assessmentBy: 'P',
    date: new Date().toISOString().split('T')[0],
    symptoms: {
      schmerz: 0,
      erschoepfung: 0,
      uebelkeit: 0,
      verstopfung: 0,
      angst: 0,
      benommenheit: 0,
      appetitlosigkeit: 0,
      wohlbefinden: 0,
      atemnot: 0,
      weitereDescherden: 0
    },
    weitereDescherdenText: '',
    visum: ''
  });
  const updateSymptom = (symptom: string, value: number) => {
    setESASData(prev => ({
      ...prev,
      symptoms: {
        ...prev.symptoms,
        [symptom]: value
      }
    }));
  };
  const resetForm = () => {
    setESASData({
      patientName: '',
      birthDate: '',
      assessmentBy: 'P',
      date: new Date().toISOString().split('T')[0],
      symptoms: {
        schmerz: 0,
        erschoepfung: 0,
        uebelkeit: 0,
        verstopfung: 0,
        angst: 0,
        benommenheit: 0,
        appetitlosigkeit: 0,
        wohlbefinden: 0,
        atemnot: 0,
        weitereDescherden: 0
      },
      weitereDescherdenText: '',
      visum: ''
    });
  };
  const printAssessment = () => {
    window.print();
  };
  const downloadPDF = () => {
    // Create a printable version
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>ESAS Assessment - ${esasData.patientName || 'Leer'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .patient-info { margin-bottom: 20px; }
            .symptoms-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .symptoms-table th, .symptoms-table td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            .score-0-2 { background-color: #dcfce7; }
            .score-3-6 { background-color: #fef3c7; }
            .score-7-10 { background-color: #fee2e2; }
            .legend { margin-bottom: 20px; }
            .legend-item { display: inline-block; margin-right: 20px; padding: 5px 10px; border: 1px solid #ccc; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Modifiziertes ESAS</h1>
            <h2>(Edmonton Symptom Assessment System)</h2>
          </div>
          
          <div class="patient-info">
            <p><strong>Name / Vorname:</strong> ${esasData.patientName}</p>
            <p><strong>Geburtsdatum:</strong> ${esasData.birthDate}</p>
            <p><strong>Datum:</strong> ${esasData.date}</p>
            <p><strong>Ausgeführt durch:</strong> ${esasData.assessmentBy === 'P' ? 'Patient/in' : esasData.assessmentBy === 'PH' ? 'Patient/in mit Hilfe' : 'Med. Fachperson'}</p>
          </div>

          <div class="legend">
            <div class="legend-item score-0-2">Grad 0-2: Symptom vorhanden, jedoch ohne Behandlungsbedarf</div>
            <div class="legend-item score-3-6">Grad 3-6: Symptom vorhanden, sollte behandelt werden</div>
            <div class="legend-item score-7-10">Grad 7-10: Symptom vorhanden, sofortiger Handlungsbedarf</div>
          </div>

          <table class="symptoms-table">
            <thead>
              <tr>
                <th>Symptom</th>
                <th>Bewertung (0-10)</th>
                <th>Handlungsempfehlung</th>
              </tr>
            </thead>
            <tbody>
              ${symptoms.map(symptom => {
        const score = esasData.symptoms[symptom.key as keyof typeof esasData.symptoms];
        const colorClass = score <= 2 ? 'score-0-2' : score <= 6 ? 'score-3-6' : 'score-7-10';
        return `
                  <tr class="${colorClass}">
                    <td>${symptom.label}</td>
                    <td>${score}</td>
                    <td>${getScoreDescription(score)}</td>
                  </tr>
                `;
      }).join('')}
            </tbody>
          </table>

          ${esasData.weitereDescherdenText ? `
            <div>
              <strong>Weitere Beschwerden (Details):</strong>
              <p>${esasData.weitereDescherdenText}</p>
            </div>
          ` : ''}

          <div style="margin-top: 30px;">
            <p><strong>Visum:</strong> ${esasData.visum}</p>
          </div>

          <div style="margin-top: 50px; font-size: 12px; color: #666;">
            <p>Kürzel für Erhebung: P = Patient/in – PH = Patient/in mit Hilfe – F = med. Fachperson</p>
            <p>Modifiziertes ESAS (Edmonton Symptom Assessment System) / Aktuelle Version: 06/2022</p>
            <p>Quelle: © modifiziert nach Spez. Palliativ-Onkologie Pflege Brückendienst, Spitex Stadt Luzern</p>
            <p>Herausgeber: Palliativ Luzern, Schachenstrasse 9, 6010 Kriens, Telefon 041 511 28 20, info@palliativ-luzern.ch, www.palliativ-luzern.ch</p>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };
  return <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-blue-800">ESAS</CardTitle>
          <p className="text-lg text-gray-600">(Edmonton Symptom Assessment System)</p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Patient Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="patientName">Name / Vorname</Label>
              <Input id="patientName" value={esasData.patientName} onChange={e => setESASData(prev => ({
              ...prev,
              patientName: e.target.value
            }))} placeholder="Name, Vorname" />
            </div>
            <div>
              <Label htmlFor="birthDate">Geburtsdatum</Label>
              <Input id="birthDate" type="date" value={esasData.birthDate} onChange={e => setESASData(prev => ({
              ...prev,
              birthDate: e.target.value
            }))} />
            </div>
            <div>
              <Label htmlFor="assessmentBy">Kürzel für Erhebung</Label>
              <Select value={esasData.assessmentBy} onValueChange={value => setESASData(prev => ({
              ...prev,
              assessmentBy: value
            }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P">P - Patient/in</SelectItem>
                  <SelectItem value="PH">PH - Patient/in mit Hilfe</SelectItem>
                  <SelectItem value="F">F - Med. Fachperson</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="date">Datum</Label>
              <Input id="date" type="date" value={esasData.date} onChange={e => setESASData(prev => ({
              ...prev,
              date: e.target.value
            }))} />
            </div>
          </div>

          <Separator />

          {/* Legend */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">Bewertungsskala (0-10):</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div className="bg-green-100 border border-green-300 p-2 rounded text-green-800">
                <strong>Grad 0-2:</strong> Symptom vorhanden, jedoch ohne Behandlungsbedarf
              </div>
              <div className="bg-yellow-100 border border-yellow-300 p-2 rounded text-yellow-800">
                <strong>Grad 3-6:</strong> Symptom vorhanden, sollte behandelt werden
              </div>
              <div className="bg-red-100 border border-red-300 p-2 rounded text-red-800">
                <strong>Grad 7-10:</strong> Symptom vorhanden, sofortiger Handlungsbedarf
              </div>
            </div>
          </div>

          {/* Symptoms Assessment */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Symptom-Bewertung</h3>
            {symptoms.map(symptom => {
            const score = esasData.symptoms[symptom.key as keyof typeof esasData.symptoms];
            return <div key={symptom.key} className={`p-4 rounded-lg border-2 ${getScoreColor(score)}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <h4 className="font-medium">{symptom.label}</h4>
                      <p className="text-sm opacity-75">{symptom.description}</p>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-medium w-8">0</span>
                        <Slider value={[score]} onValueChange={value => updateSymptom(symptom.key, value[0])} max={10} step={1} className="flex-1" />
                        <span className="text-sm font-medium w-8">10</span>
                        <div className="w-12 text-center">
                          <span className="text-lg font-bold">{score}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-sm font-medium">
                    {getScoreDescription(score)}
                  </div>
                </div>;
          })}
          </div>

          {/* Additional Complaints */}
          <div>
            <Label htmlFor="weitereDescherden">Weitere Beschwerden (Details)</Label>
            <Textarea id="weitereDescherden" value={esasData.weitereDescherdenText} onChange={e => setESASData(prev => ({
            ...prev,
            weitereDescherdenText: e.target.value
          }))} placeholder="Beschreiben Sie weitere Beschwerden oder Symptome..." rows={3} />
          </div>

          {/* Signature */}
          <div>
            <Label htmlFor="visum">Visum (Unterschrift/Kürzel)</Label>
            <Input id="visum" value={esasData.visum} onChange={e => setESASData(prev => ({
            ...prev,
            visum: e.target.value
          }))} placeholder="Unterschrift oder Kürzel" />
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={printAssessment} className="flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Drucken
            </Button>
            <Button onClick={downloadPDF} variant="outline" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Als PDF herunterladen
            </Button>
            <Button onClick={resetForm} variant="outline" className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4" />
              Formular zurücksetzen
            </Button>
          </div>

          {/* Footer Information */}
          <div className="text-xs text-gray-500 border-t pt-4">
            <p><strong>Kürzel für Erhebung:</strong> P = Patient/in – PH = Patient/in mit Hilfe – F = med. Fachperson</p>
            <p>Modifiziertes ESAS (Edmonton Symptom Assessment System)</p>
            
            <p>Nur für den persönlichen Gebrauch bestimmt!</p>
          </div>
        </CardContent>
      </Card>
    </div>;
};